import React, { useCallback, useEffect } from 'react';
import {
  ErrorDisplay,
  Feedback,
  FormDatePicker,
  FormHub,
  FormTextField,
  Section,
  SubmitButton,
  useFormEvents,
  useObservable,
} from '@lib/common';
import { Grid } from '@material-ui/core';
import { getDate } from 'app/shared';
import { FormStatus, sessionQuery } from '@lib/state';
import { ReportForms, reportService } from 'app/state';
import { useFormContext } from 'react-hook-form';
import { differenceInCalendarDays, parseISO } from 'date-fns';

// Keep in sync with TransactionsNotInvoicedReportRequest.MaxRangeDays on the backend.
const MAX_RANGE_DAYS = 92;

interface Props {
  status: FormStatus;
  error?: Error;
  resetFormUI: () => void;
}

const UnbilledReservationsReportDownload: React.FC<Props> = ({
  status,
  resetFormUI,
  error,
}: Props) => {
  const { setValue, getValues, watch, errors, clearError } = useFormContext();
  const accountingNotifications = useObservable(sessionQuery.accountingNotifications);

  useEffect(() => {
    if (accountingNotifications) setValue('recipients', accountingNotifications);
  }, [accountingNotifications, setValue]);

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  useEffect(() => {
    // FormDatePicker registers without a DOM ref, so triggerValidation(name) can't re-check it -
    // clear the stale "range too large" message directly once the range becomes valid again.
    if (!errors.endDate || !startDate || !endDate) return;
    const days = differenceInCalendarDays(parseISO(endDate), parseISO(startDate));
    if (days < MAX_RANGE_DAYS) clearError('endDate');
  }, [startDate, endDate, errors.endDate, clearError]);

  return (
    <Grid container spacing={2}>
      <Grid item md={2} xs={6}>
        <FormDatePicker
          name="startDate"
          label="Start Date"
          fullWidth
          validationOptions={{ required: true }}
          format="MM/dd/yyyy"
          autoOk
        />
      </Grid>
      <Grid item md={2} xs={6}>
        <FormDatePicker
          name="endDate"
          label="End Date"
          fullWidth
          validationOptions={{
            required: true,
            validate: value => {
              const start = getValues('startDate');
              if (!start || !value) return true;
              const days = differenceInCalendarDays(parseISO(value), parseISO(start));
              return (
                days < MAX_RANGE_DAYS ||
                `Date range cannot exceed ${MAX_RANGE_DAYS} days. Please select a smaller range.`
              );
            },
          }}
          format="MM/dd/yyyy"
          autoOk
        />
      </Grid>
      <Grid item xs={4}>
        <FormTextField label="Receipents" type="text" name="recipients" required fullWidth />
      </Grid>
      <Grid container item xs={2} spacing={1}>
        <Grid item>
          <SubmitButton
            type="submit"
            color="secondary"
            variant="contained"
            disabled={status === FormStatus.Pending}
          >
            Send Report
          </SubmitButton>
        </Grid>
        <Grid item>
          <Feedback show={status === FormStatus.Success} severity="success" onHide={resetFormUI}>
            Unbilled Transaction Report Requested
          </Feedback>
          <ErrorDisplay error={error} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export interface UnbilledReservationsReportFields {
  startDate: string;
  endDate: string;
  recipients: string;
}

interface ReportProps {
  customerId: string;
  propertyId: string;
}

export function UnbilledReservationsReport({ customerId, propertyId }: ReportProps) {
  const [{ status, error }, resetUI] = useFormEvents(ReportForms.UnbilledTransactions);

  const onSubmit = useCallback(
    (fields: UnbilledReservationsReportFields) => {
      if (propertyId) {
        reportService.exportUnbilledReservations(
          propertyId,
          getDate(fields.startDate),
          getDate(fields.endDate),
          fields.recipients
        );
      }
    },
    [propertyId]
  );

  if (!propertyId) return null;

  return (
    <>
      <Section title="Unbilled Transactions">
        <FormHub<UnbilledReservationsReportFields> onSubmit={onSubmit}>
          <UnbilledReservationsReportDownload status={status} resetFormUI={resetUI} error={error} />
        </FormHub>
      </Section>
    </>
  );
}
